import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import projectService from '../api/projectService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SketchPicker } from 'react-color';
import { usePermissions } from '../hooks/usePermissions';
import { ReadOnlyIndicator } from '../components/ui/PermissionWrapper';

const SDKIntegration = () => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  const permissions = usePermissions();

  // Check permissions  
  const canManageSDK = permissions.canManage('sdkIntegration');
  const canViewSDK = permissions.canView('sdkIntegration');
  const hasReadOnlyAccess = canViewSDK && !canManageSDK;

  // State for all customization options
  const [customCss, setCustomCss] = useState('');
  const [initialMessage, setInitialMessage] = useState('Hi there! How can I help you today?');
  const [chatbotName, setChatbotName] = useState('Assistant');
  const [logo, setLogo] = useState('');
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  const [borderColor, setBorderColor] = useState('#E5E7EB');
  const [textColor, setTextColor] = useState('#1F2937');
  const [fontFamily, setFontFamily] = useState('Inter, sans-serif');
  const [fontSize, setFontSize] = useState('14px');
  const [primaryColor, setPrimaryColor] = useState('#3B82F6');
  const [secondaryColor, setSecondaryColor] = useState('#F3F4F6');
  const [buttonColor, setButtonColor] = useState('#3B82F6');
  const [buttonTextColor, setButtonTextColor] = useState('#FFFFFF');
  const [rateLimitMessage, setRateLimitMessage] = useState('Message rate limit exceeded. Please wait before sending more messages.');
  const [placeholderText, setPlaceholderText] = useState('Type your message...');
  const [integrationType, setIntegrationType] = useState('html');
  const [whatsappIntegrationType, setWhatsappIntegrationType] = useState('cloud');
  const [isEmbeddedMode, setIsEmbeddedMode] = useState(false); // New: embedded mode toggle
  const [whatsappConfig, setWhatsappConfig] = useState({
    // Common fields
    providerType: 'cloud',
    webhookSecret: '',
    webhookUrl: '',
    // Cloud API fields
    phoneNumberId: '',
    accessToken: '',
    // Twilio fields
    accountSid: '',
    authToken: '',
    phoneNumber: ''
  });

  // Color picker state
  const [activeColorPicker, setActiveColorPicker] = useState(null);

  // State for SDK script URL
  const [sdkUrl, setSdkUrl] = useState('');
  const [isGeneratingSDK, setIsGeneratingSDK] = useState(false);

  // Fetch project data to check rate limiting status
  const { data: projectData, isLoading: isProjectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectService.getProject(projectId),
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window gains focus
  });
  
  // Derived state for rate limiting
  const [isRateLimitingEnabled, setIsRateLimitingEnabled] = useState(false);
  
  // Update rate limiting status when project data changes
  useEffect(() => {
    if (projectData && projectData.data && projectData.data.project) {
      const project = projectData.data.project;
      const isEnabled = project.rateLimiting?.enabled || false;
      
      // Update rate limiting status
      setIsRateLimitingEnabled(isEnabled);
      
      // Also update rate limit message if available
      if (project.rateLimiting?.customMessage) {
        setRateLimitMessage(project.rateLimiting.customMessage);
      }
      
      console.log('Project data refreshed - Rate limiting enabled:', isEnabled);
    }
  }, [projectData]);

  // Fetch SDK config
  const { data: sdkConfig, isLoading, error: queryError } = useQuery({
    queryKey: ['sdkConfig', projectId],
    queryFn: async () => {
      console.log('Fetching SDK config for project:', projectId);
      try {
        const result = await projectService.getSDKConfig(projectId);
        console.log('SDK Config raw result STRUCTURE:', JSON.stringify(result));
        console.log('SDK Config directly:', result);
        return result;
      } catch (error) {
        console.error('Error in queryFn:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('SDK Config onSuccess called with data:', data);
      
      // At this point, data should be the config object directly
      // Make sure it's an object before trying to use it
      if (data && typeof data === 'object') {
        console.log('Setting SDK config values:', data);
        // Set all values, falling back to defaults if values are missing
        setCustomCss(data.customCss || '');
        setInitialMessage(data.initialMessage || 'Hi there! How can I help you today?');
        setChatbotName(data.chatbotName || 'Assistant');
        setLogo(data.logo || '');
        setBackgroundColor(data.backgroundColor || '#FFFFFF');
        setBorderColor(data.borderColor || '#E5E7EB');
        setTextColor(data.textColor || '#1F2937');
        setFontFamily(data.fontFamily || 'Inter, sans-serif');
        setFontSize(data.fontSize || '14px');
        setPrimaryColor(data.primaryColor || '#3B82F6');
        setSecondaryColor(data.secondaryColor || '#F3F4F6');
        setButtonColor(data.buttonColor || '#3B82F6');
        setButtonTextColor(data.buttonTextColor || '#FFFFFF');
        setPlaceholderText(data.placeholderText || 'Type your message...');
        
        // Set rate limit message if available
        if (data.project && data.project.rateLimiting) {
          setRateLimitMessage(data.project.rateLimiting.customMessage || 'Message rate limit exceeded. Please wait before sending more messages.');
        }
        
        // Set SDK URL if available
        if (data.sdkUrl) {
          setSdkUrl(data.sdkUrl);
          console.log('Found existing SDK URL:', data.sdkUrl);
        }
      } else {
        console.warn('Invalid SDK config data received:', data);
      }
    },
    onError: (error) => {
      console.error('Error fetching SDK config:', error);
      toast.error('Failed to load configuration settings', {
        position: "top-right",
        autoClose: 5000
      });
    },
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window gains focus
  });

  // Use useEffect as a fallback to handle the data if onSuccess isn't called
  useEffect(() => {
    if (sdkConfig && !isLoading) {
      console.log('SDKConfig data from useEffect - TYPE:', typeof sdkConfig);
      console.log('SDKConfig data from useEffect - KEYS:', Object.keys(sdkConfig));
      console.log('SDKConfig data from useEffect - VALUES:', JSON.stringify(sdkConfig));
      
      // Apply the same logic as in onSuccess
      if (typeof sdkConfig === 'object') {
        console.log('Setting chatbotName to:', sdkConfig.chatbotName || 'Assistant');
        console.log('Setting initialMessage to:', sdkConfig.initialMessage || 'Hi there! How can I help you today?');
        
        setCustomCss(sdkConfig.customCss || '');
        setInitialMessage(sdkConfig.initialMessage || 'Hi there! How can I help you today?');
        setChatbotName(sdkConfig.chatbotName || 'Assistant');
        setLogo(sdkConfig.logo || '');
        setBackgroundColor(sdkConfig.backgroundColor || '#FFFFFF');
        setBorderColor(sdkConfig.borderColor || '#E5E7EB');
        setTextColor(sdkConfig.textColor || '#1F2937');
        setFontFamily(sdkConfig.fontFamily || 'Inter, sans-serif');
        setFontSize(sdkConfig.fontSize || '14px');
        setPrimaryColor(sdkConfig.primaryColor || '#3B82F6');
        setSecondaryColor(sdkConfig.secondaryColor || '#F3F4F6');
        setButtonColor(sdkConfig.buttonColor || '#3B82F6');
        setButtonTextColor(sdkConfig.buttonTextColor || '#FFFFFF');
        setPlaceholderText(sdkConfig.placeholderText || 'Type your message...');
        
        // Set rate limit message if available
        if (sdkConfig.project && sdkConfig.project.rateLimiting) {
          setRateLimitMessage(sdkConfig.project.rateLimiting.customMessage || 'Message rate limit exceeded. Please wait before sending more messages.');
        }
        
        // Set SDK URL if available
        if (sdkConfig.sdkUrl) {
          setSdkUrl(sdkConfig.sdkUrl);
          console.log('Found existing SDK URL from useEffect:', sdkConfig.sdkUrl);
        }
      }
    }
  }, [sdkConfig, isLoading]);

  // Display loading or error states
  useEffect(() => {
    if (queryError) {
      console.error('Query error detected:', queryError);
    }
  }, [queryError]);

  // Update SDK config mutation
  const { mutate: updateConfig, isLoading: isSaving } = useMutation({
    mutationFn: (data) => projectService.updateSDKConfig(projectId, data),
    onSuccess: () => {
      // Show success toast
      toast.success('Chatbot configuration saved successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Invalidate the query to refresh the data
      queryClient.invalidateQueries(['sdkConfig', projectId]);
    },
    onError: (error) => {
      // Show error toast
      toast.error(`Failed to save configuration: ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  });

  // Add this after the existing state declarations
  const { mutate: saveWhatsAppConfig, isLoading: isSavingWhatsApp } = useMutation({
    mutationFn: (config) => projectService.saveWhatsAppConfig(projectId, config),
    onSuccess: (data) => {
      // Create a merged configuration that preserves existing fields
      // while updating with new data from the server
      const serverData = data.data || data;
      const providerType = serverData.providerType || whatsappConfig.providerType || 'cloud';
      
      console.log('WhatsApp config saved successfully:', {
        providerType,
        webhookUrl: serverData.webhookUrl,
        hasData: !!serverData
      });
      
      // Create merged config based on provider type
      const mergedConfig = {
        ...whatsappConfig,  // Keep existing values
        providerType,       // Update provider type
        webhookUrl: serverData.webhookUrl || whatsappConfig.webhookUrl,
        webhookSecret: serverData.webhookSecret || whatsappConfig.webhookSecret
      };
      
      // Update state with merged configuration
      setWhatsappConfig(mergedConfig);
      
      toast.success('WhatsApp configuration saved successfully!', {
        position: "top-right",
        autoClose: 3000,
      });
    },
    onError: (error) => {
      toast.error(`Failed to save WhatsApp configuration: ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  });

  // Add this after the existing useQuery for sdkConfig
  console.log(`Integration type: ${integrationType}`); // Log the current integration type
  
  const { data: whatsappData, isError: whatsappError, error: whatsappErrorDetails } = useQuery({
    queryKey: ['whatsappConfig', projectId],
    queryFn: () => {
      console.log('Executing WhatsApp config query function');
      return projectService.getWhatsAppConfig(projectId);
    },
    onSuccess: (data) => {
      console.log("Updating Whatsapp Config from onSuccess!", data);
      
      // Direct update on successful data fetch
      if (data) {
        try {
          // Handle different possible data structures
          const configData = data.data || data;
          
          console.log("Extracted WhatsApp config data:", configData);
          
          // Set provider type first and update UI dropdown
          const providerType = configData.providerType || 'cloud';
          setWhatsappIntegrationType(providerType);
          
          // Create base config with common fields
          const newConfig = {
            providerType: providerType,
            webhookSecret: configData.webhookSecret || '',
            webhookUrl: configData.webhookUrl || ''
          };
          
          // Add provider-specific fields
          if (providerType === 'twilio') {
            // Twilio fields
            newConfig.accountSid = configData.accountSid || '';
            newConfig.authToken = configData.authToken || '';
            newConfig.phoneNumber = configData.phoneNumber || '';
            console.log('Loaded Twilio configuration:', {
              accountSid: newConfig.accountSid,
              hasAuthToken: !!newConfig.authToken,
              phoneNumber: newConfig.phoneNumber
            });
          } else {
            // Cloud API fields
            newConfig.phoneNumberId = configData.phoneNumberId || '';
            newConfig.accessToken = configData.accessToken || '';
            console.log('Loaded WhatsApp Cloud API configuration:', {
              phoneNumberId: newConfig.phoneNumberId,
              hasAccessToken: !!newConfig.accessToken
            });
          }
          
          // Update state with the complete config
          setWhatsappConfig(newConfig);
        } catch (err) {
          console.error("Error parsing WhatsApp config data:", err);
        }
      }
    },
    onError: (error) => {
      console.error("Error fetching WhatsApp config:", error);
    },
    enabled: integrationType === 'whatsapp'
  });
  
  // Direct useEffect to update state when data changes
  useEffect(() => {
    if (whatsappData) {
      console.log("WhatsApp data changed, updating state directly:", whatsappData);
      
      try {
        // Handle different possible data structures
        const configData = whatsappData.data || whatsappData;
        
        // Set provider type first and update UI dropdown
        const providerType = configData.providerType || 'cloud';
        setWhatsappIntegrationType(providerType);
        
        // Create base config with common fields
        const newConfig = {
          providerType: providerType,
          webhookSecret: configData.webhookSecret || '',
          webhookUrl: configData.webhookUrl || ''
        };
        
        // Add provider-specific fields
        if (providerType === 'twilio') {
          // Twilio fields
          newConfig.accountSid = configData.accountSid || '';
          newConfig.authToken = configData.authToken || '';
          newConfig.phoneNumber = configData.phoneNumber || '';
          console.log('Loaded Twilio configuration:', {
            accountSid: newConfig.accountSid,
            hasAuthToken: !!newConfig.authToken,
            phoneNumber: newConfig.phoneNumber
          });
        } else {
          // Cloud API fields
          newConfig.phoneNumberId = configData.phoneNumberId || '';
          newConfig.accessToken = configData.accessToken || '';
          console.log('Loaded WhatsApp Cloud API configuration:', {
            phoneNumberId: newConfig.phoneNumberId,
            hasAccessToken: !!newConfig.accessToken
          });
        }
        
        // Update state with the complete config
        setWhatsappConfig(newConfig);
      } catch (err) {
        console.error("Error updating WhatsApp config from useEffect:", err);
      }
    }
  }, [whatsappData]);
  
  // Additional debugging - watch for state changes
  useEffect(() => {
    console.log("WhatsappConfig state changed:", {
      providerType: whatsappConfig.providerType,
      // Cloud API fields
      phoneNumberId: whatsappConfig.phoneNumberId,
      accessToken: whatsappConfig.accessToken ? "********" : "",
      // Twilio fields
      accountSid: whatsappConfig.accountSid,
      authToken: whatsappConfig.authToken ? "********" : "",
      phoneNumber: whatsappConfig.phoneNumber,
      // Common fields
      webhookSecret: whatsappConfig.webhookSecret,
      webhookUrl: whatsappConfig.webhookUrl
    });
  }, [whatsappConfig]);
  
  // Additional debugging - watch for integration type changes
  useEffect(() => {
    console.log(`Integration type changed to: ${integrationType}`);
    
    // Force refetch when integration type changes to whatsapp
    if (integrationType === 'whatsapp') {
      queryClient.invalidateQueries(['whatsappConfig', projectId]);
    }
  }, [integrationType, projectId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create the SDK configuration object
    const sdkConfig = {
      customCss,
      initialMessage,
      chatbotName,
      logo,
      backgroundColor,
      borderColor,
      textColor,
      fontFamily,
      fontSize,
      primaryColor,
      secondaryColor,
      buttonColor,
      buttonTextColor,
      placeholderText
    };
    
    // If rate limiting is enabled, include the custom message
    if (isRateLimitingEnabled) {
      // We need to update the project's rateLimiting configuration
      projectService.updateProject(projectId, {
        rateLimiting: {
          ...projectData?.data?.project?.rateLimiting,
          customMessage: rateLimitMessage
        }
      })
      .then(() => {
        console.log('Rate limit message updated');
        // Invalidate the project and SDK config queries to refresh data
        queryClient.invalidateQueries(['project', projectId]);
        queryClient.invalidateQueries(['sdkConfig', projectId]);
      })
      .catch(error => {
        console.error('Error updating rate limit message:', error);
      });
    }
    
    // Update the SDK configuration
    updateConfig(sdkConfig);
  };

  // Add a component to show SDK URL info
  const SDKUrlInfo = () => {
    if (!sdkUrl) return null;
    
    return (
      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded p-2">
        <p className="text-sm text-green-800 mb-2">
          <strong>Using existing SDK script:</strong>
        </p>
        <p className="text-sm text-gray-700 break-all">
          Your SDK script is available at: <a href={sdkUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{sdkUrl}</a>
        </p>
        <p className="text-xs text-gray-500 mt-2">
          To regenerate the SDK with your latest changes, click the "Generate SDK Script" button.
        </p>
      </div>
    );
  };

  // Generate example code snippets
  const getHtmlSnippet = () => `
<!-- Add this to your HTML file -->
<script src="${sdkUrl || `${window.location.origin}/sdk/${projectId}.js`}"></script>
<div id="neochat-widget"></div>

<script>
  // Initialize the chatbot
  NeoChat.init({
    projectId: '${projectId}', // You can pass any projectId here to override the SDK's default
    container: '#neochat-widget',
    position: 'bottom-right', // or 'bottom-left'
    theme: 'light', // or 'dark'
    // Optional: Handle intermediate messages
    onIntermediateMessage: function(message) {
      console.log('Received intermediate message:', message.content);
      // You can use this to show a custom processing state or visual feedback
    }
  });

  // Optional: Set user information
  NeoChat.setUser({
    id: 'user123', // Your user's ID
    name: 'John Doe', // Your user's name
    email: 'john@example.com', // Your user's email
    metadata: { // Any additional user data
      plan: 'premium',
      company: 'Acme Inc',
      // Authentication data for API endpoints (if configured)
      token: 'your-jwt-token-here',
      apiKey: 'your-api-key-here'
    }
  });

  // Optional: Listen for intermediate messages and typing events
  window.addEventListener('neochat:intermediate', function(event) {
    // This fires when the chatbot sends an intermediate response
    console.log('Intermediate message:', event.detail.message);
  });

  window.addEventListener('neochat:typing', function(event) {
    // This fires when the chatbot is typing
    console.log('Chatbot is typing...');
  });
</script>
  `.trim();

  const getReactSnippet = (isEmbedded) => `
// React Integration Example ${isEmbedded ? '(Embedded Mode)' : '(Widget Mode)'}
// 🔥 NEW: You can pass any projectId to override the SDK's default!

import React, { useEffect, useRef, useState } from 'react';

${isEmbedded ? `// EMBEDDED MODE - Custom Chat Container
const NeoChatEmbedded = ({ userId, userName, userEmail, projectId = '${projectId}', className, style }) => {
  const scriptLoaded = useRef(false);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    if (!scriptLoaded.current) {
      const script = document.createElement('script');
      script.src = "${sdkUrl || `${window.location.origin}/sdk/${projectId}.js`}";
      script.async = true;
      script.onload = () => {
        if (window.NeoChat) {
          window.NeoChat.init({
            projectId: projectId, // 🔥 Use passed projectId - overrides SDK default!
            container: '#neochat-embedded',
            mode: 'embedded', // 🎯 EMBEDDED MODE - No default styling!
            onMessage: (message) => {
              setMessages(prev => [...prev, message]);
              setIsTyping(false);
            },
            onIntermediateMessage: (message) => {
              console.log('Intermediate:', message.content);
            },
            onTyping: () => {
              setIsTyping(true);
            }
          });

          if (userId) {
            window.NeoChat.setUser({
              id: userId,
              name: userName,
              email: userEmail,
              metadata: {
                token: 'your-jwt-token-here' // Add your JWT token here
              }
            });
          }
        }
      };
      document.body.appendChild(script);
      scriptLoaded.current = true;
    }
  }, [userId, userName, userEmail, projectId]);

  // Function to send message programmatically
  const sendMessage = (message) => {
    if (window.NeoChat) {
      window.NeoChat.sendMessage(message);
    }
  };

  return (
    <div className={\`your-custom-chat-container \${className || ''}\`} style={{
      width: '100%',
      height: '400px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      display: 'flex',
      flexDirection: 'column',
      ...style
    }}>
      {/* Your custom header */}
      <div style={{
        padding: '10px',
        borderBottom: '1px solid #ddd',
        background: '#f5f5f5',
        flexShrink: 0
      }}>
        <h3 style={{ margin: 0 }}>Custom Chat Header</h3>
        {isTyping && <small>Bot is typing...</small>}
      </div>
      
      {/* NeoChat embedded container - 100% width, no headers */}
      <div id="neochat-embedded" style={{ 
        height: 'calc(100% - 50px)',
        width: '100%'
      }}></div>
      
      {/* Optional: Custom send button */}
      <div style={{ padding: '10px', borderTop: '1px solid #ddd', flexShrink: 0 }}>
        <button onClick={() => sendMessage('Hello!')} style={{
          padding: '8px 16px',
          background: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}>
          Send Hello
        </button>
      </div>
    </div>
  );
};

// Usage with Dynamic ProjectId:
function App() {
  const currentProjectId = 'your-actual-project-id'; // Get this from your app logic
  
  return (
    <div>
      <h1>My App with Custom NeoChat</h1>
      <NeoChatEmbedded 
        userId="user123"
        userName="John Doe"
        userEmail="john@example.com"
        projectId={currentProjectId} // Pass the projectId as a prop
        className="my-chat"
        style={{ height: '500px' }}
      />
    </div>
  );
}` : `// WIDGET MODE - Floating Chat Button
const NeoChatWidget = ({ userId, userName, userEmail }) => {
  const scriptLoaded = useRef(false);
  const [intermediateMessage, setIntermediateMessage] = useState(null);

  useEffect(() => {
    if (!scriptLoaded.current) {
      const script = document.createElement('script');
      script.src = "${sdkUrl || `${window.location.origin}/sdk/${projectId}.js`}";
      script.async = true;
      script.onload = () => {
        if (window.NeoChat) {
          window.NeoChat.init({
            projectId: '${projectId}',
            container: 'body', // Widget mode uses body
            position: 'bottom-right',
            theme: 'light',
            onIntermediateMessage: (message) => {
              setIntermediateMessage(message);
            }
          });

          if (userId) {
            window.NeoChat.setUser({
              id: userId,
              name: userName,
              email: userEmail,
              metadata: {
                token: 'your-jwt-token-here' // Add your JWT token here
              }
            });
          }
          
          window.addEventListener('neochat:intermediate', (event) => {
            if (event.detail && event.detail.message) {
              setIntermediateMessage(event.detail.message);
            }
          });
          
          window.addEventListener('neochat:typing', () => {
            console.log('Chatbot is typing...');
          });
        }
      };
      document.body.appendChild(script);
      scriptLoaded.current = true;
    }

    return () => {
      window.removeEventListener('neochat:intermediate', () => {});
      window.removeEventListener('neochat:typing', () => {});
    };
  }, [userId, userName, userEmail]);

  useEffect(() => {
    if (intermediateMessage) {
      console.log('Intermediate message received:', intermediateMessage.content);
    }
  }, [intermediateMessage]);

  return null; // Widget mode doesn't need a container element
};

// Usage:
function App() {
  return (
    <div className="App">
      <h1>My React App with NeoChat</h1>
      <p>The chat widget will appear as a floating button in the bottom-right corner.</p>
      <NeoChatWidget 
        userId="user123"
        userName="John Doe"
        userEmail="john@example.com"
      />
    </div>
  );
}`}

export default App;`;

  const getFlutterSnippet = (isEmbedded) => `
// Flutter Integration Example using WebView with improved compatibility

// First, add webview_flutter to your pubspec.yaml:
// dependencies:
//   flutter:
//     sdk: flutter
//   webview_flutter: ^4.0.0

import 'package:flutter/material.dart';
import 'package:webview_flutter/webview_flutter.dart';
import 'dart:convert';

void main() {
  runApp(const MyApp());
}

class MyApp extends StatelessWidget {
  const MyApp({super.key});

  @override
  Widget build(BuildContext context) {
    return MaterialApp(
      debugShowCheckedModeBanner: false,
      title: 'NeoChat Test App',
      theme: ThemeData(primarySwatch: Colors.blue),
      home: const HomeScreen(),
    );
  }
}

class HomeScreen extends StatelessWidget {
  const HomeScreen({super.key});

  @override
  Widget build(BuildContext context) {
    return Scaffold(
      appBar: AppBar(title: const Text('NeoChat Test')),
      body: Center(
        child: ElevatedButton(
          onPressed: () {
            Navigator.push(
              context,
              MaterialPageRoute(builder: (context) => const NeoChatScreen(
                userId: 'flutter-user-123',
                name: 'John Doe',
                userEmail: 'john@example.com',
              )),
            );
          },
          child: const Text('Launch NeoChat'),
        ),
      ),
    );
  }
}

class NeoChatScreen extends StatefulWidget {
  final String userId;
  final String name;
  final String userEmail;
  
  const NeoChatScreen({
    Key? key, 
    this.userId = 'flutter-user-123',
    this.name = 'John Doe',
    this.userEmail = 'john@example.com',
  }) : super(key: key);

  @override
  State<NeoChatScreen> createState() => _NeoChatScreenState();
}

class _NeoChatScreenState extends State<NeoChatScreen> {
  late final WebViewController controller;
  final List<String> consoleLogs = [];
  String? intermediateMessage;
  bool isTyping = false;
  bool isInitialized = false;

  @override
  void initState() {
    super.initState();
    
    // Properly encode user data
    final userIdJson = jsonEncode(widget.userId);
    final nameJson = jsonEncode(widget.name);
    final userEmailJson = jsonEncode(widget.userEmail);
    
    print("User data being passed to webview: ID=$userIdJson, Name=$nameJson, Email=$userEmailJson");
    
    // Create a controller for the WebView
    controller = WebViewController()
      ..setJavaScriptMode(JavaScriptMode.unrestricted)
      ..setBackgroundColor(const Color(0x00000000))
      ..setNavigationDelegate(
        NavigationDelegate(
          onPageFinished: (String url) {
            // Initialize chat with user data
            print("WebView page finished loading, initializing chat with user data");
            
            // Use a simpler JavaScript initialization approach with better error handling
            controller.runJavaScript('''
              try {
                // First, add userData object to window for later use
                window.flutterUserData = {
                  id: $userIdJson,
                  name: $nameJson,
                  email: $userEmailJson
                };
                
                // Signal to Flutter that we are ready
                window.NeoChatFlutter.postMessage(JSON.stringify({
                  type: 'debug_init_params',
                  userId: $userIdJson,
                  name: $nameJson,
                  userEmail: $userEmailJson,
                  timestamp: new Date().toISOString()
                }));
                
                // Simple logging
                console.log("Setting up message monitoring - safer version");
                
                // Directly set the user data in NeoChat
                if (window.NeoChat) {
                  window.NeoChat.userData = window.flutterUserData;
                  
                  // Then initialize with setUser
                  window.NeoChat.setUser(window.flutterUserData);
                  
                  // Wait a bit and then call init
                  setTimeout(function() {
                    if (window.NeoChat && window.NeoChat.init) {
                      window.NeoChat.init({
                        projectId: "${projectId}",
                        container: "#neochat-container",
                        position: "center",
                        theme: "light",
                        user: window.flutterUserData,
                        showCloseButton: true // Ensure the close button is shown
                      });

                      // Log successful initialization
                      window.NeoChatFlutter.postMessage(JSON.stringify({
                        type: 'debug_init',
                        message: 'NeoChat initialized with user data',
                        userData: window.flutterUserData,
                        timestamp: new Date().toISOString()
                      }));

                      // Fix chat button position after initialization
                      setTimeout(function() {
                        const widgetButton = document.querySelector('.neochat-widget-button');
                        if (widgetButton) {
                          widgetButton.style.position = 'fixed';
                          widgetButton.style.bottom = '20px';
                          widgetButton.style.right = '20px';
                          widgetButton.style.transform = 'none';
                          widgetButton.style.zIndex = '9999';
                        }
                      }, 500);
                    }
                  }, 500);
                } else {
                  console.error("NeoChat object not found");
                }
                
                // Simple DOM observer for messages
                setTimeout(function() {
                  try {
                    const chatContainer = document.querySelector('.neochat-chat-messages');
                    if (chatContainer) {
                      // Make sure chat messages are scrollable
                      chatContainer.style.overflowY = 'auto';
                      chatContainer.style.webkitOverflowScrolling = 'touch';
                      
                      const observer = new MutationObserver(function(mutations) {
                        mutations.forEach(function(mutation) {
                          for (let i = 0; i < mutation.addedNodes.length; i++) {
                            const node = mutation.addedNodes[i];
                            if (node.nodeType === 1 && node.classList && node.classList.contains('neochat-bot-message')) {
                              const isIntermediate = node.classList.contains('neochat-intermediate');
                              const messageText = node.textContent || node.innerText;
                              
                              window.NeoChatFlutter.postMessage(JSON.stringify({
                                type: isIntermediate ? 'intermediate_message' : 'bot_message',
                                content: messageText,
                                timestamp: new Date().toISOString()
                              }));
                              
                              // Scroll to bottom when new message appears
                              chatContainer.scrollTop = chatContainer.scrollHeight;
                            }
                          }
                        });
                      });
                      
                      observer.observe(chatContainer, { childList: true, subtree: true });
                      console.log("Message observer set up successfully");
                    } else {
                      console.warn("Chat container not found yet");
                    }
                  } catch (err) {
                    console.error("Error setting up message observer:", err);
                  }
                }, 1000);
              } catch (e) {
                console.error("Error in initialization:", e);
                window.NeoChatFlutter.postMessage(JSON.stringify({
                  type: 'error',
                  message: e.toString(),
                  timestamp: new Date().toISOString()
                }));
              }
            ''').catchError((err) {
              print("Error executing JavaScript: \${err}");
              setState(() {
                consoleLogs.add("Error executing JavaScript: \${err}");
              });
            });
          },
          onWebResourceError: (WebResourceError err) {
            print("WebView error: \${err.description}");
            setState(() {
              consoleLogs.add("WebView error: \${err.description}");
            });
          },
        ),
      )
      ..addJavaScriptChannel(
        'NeoChatFlutter',
        onMessageReceived: (JavaScriptMessage msg) {
          // Handle messages from JavaScript
          print('Message from JS: \${msg.message}');
          
          try {
            final dynamic data = json.decode(msg.message);
            
            // Handle different message types
            if (data is Map && data.containsKey('type')) {
              switch(data['type']) {
                case 'intermediate_message':
                  // Handle intermediate message
                  setState(() {
                    intermediateMessage = data['content'];
                    // Don't hide typing indicator for intermediate messages
                    if (!isTyping) {
                      isTyping = true;
                    }
                  });
                  break;
                  
                case 'typing_indicator':
                  // Handle typing indicator
                  setState(() {
                    isTyping = true;
                  });
                  break;
                
                case 'error':
                  // Handle error messages
                  setState(() {
                    consoleLogs.add("ERROR: \${data['message']}");
                  });
                  break;
                  
                case 'message':
                  setState(() {
                    intermediateMessage = null;
                    isTyping = false;
                  });
                  break;
                
                default:
                  // Add all messages to console logs
                  setState(() {
                    consoleLogs.add(msg.message);
                  });
              }
            } else {
              // Add to general logs if not a special message type
              setState(() {
                consoleLogs.add(msg.message);
              });
            }
          } catch (e) {
            // For non-JSON messages, just add to logs
            setState(() {
              consoleLogs.add("Error parsing message: \${e}\\nOriginal message: \${msg.message}");
            });
          }
        },
      );
    
    // Load the HTML content
    controller.loadHtmlString(_buildHtmlContent());
  }
  
  String _buildHtmlContent() {
    // Create HTML content with the NeoChat SDK embedded - simpler version
    return '''
      <!DOCTYPE html>
      <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
        <style>
          body, html { 
            margin: 0; 
            padding: 0; 
            height: 100%; 
            width: 100%; 
            overflow: hidden;
            position: relative;
            background-color: #f9f9f9;
          }
          #neochat-container { 
            width: 100%; 
            height: 100%; 
            position: relative;
          }
          .neochat-chat-container {
            height: calc(100% - 80px) !important;
            max-height: none !important;
            position: fixed !important;
            top: 0 !important;
            left: 5% !important;
            right: 5% !important;
            bottom: 0 !important;
            width: 90% !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            display: none !important;
            z-index: 9999 !important;
          }
          .neochat-chat-container.active {
            display: flex !important;
            flex-direction: column !important;
          }
          .neochat-chat-header {
            position: relative !important;
            z-index: 10000 !important;
            background-color: #4a90e2 !important;
            color: white !important;
            padding: 15px !important;
            border-bottom: 1px solid #eee !important;
          }
          .neochat-chat-header h3 {
            color: white !important;
            margin: 0 !important;
            font-size: 16px !important;
            font-weight: 600 !important;
          }
          .neochat-chat-body {
            flex: 1 !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch !important;
            position: relative !important;
            height: calc(100% - 120px) !important;
          }
          .neochat-chat-messages {
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch !important;
            padding-bottom: 20px !important;
            flex: 1 !important;
            display: flex !important;
            flex-direction: column !important;
            position: relative !important;
            gap: 10px !important;
          }
          .neochat-typing-indicator {
            position: relative !important;
            margin: 0 !important;
            background-color: #f5f5f5 !important;
            border-radius: 8px !important;
            display: none !important;
            max-width: 50% !important;
            align-self: flex-start !important;
            order: 9999 !important;
            margin-left: 10px !important;
          }
          .neochat-typing-indicator.active {
            display: block !important;
          }
          .neochat-bot-message, .neochat-user-message {
            margin: 0 !important;
            padding: 10px !important;
            max-width: 80% !important;
            border-radius: 8px !important;
          }
          .neochat-widget-button {
            position: fixed !important;
            bottom: 20px !important;
            right: 20px !important;
            z-index: 9999 !important;
            transform: none !important;
          }
          .neochat-chat-container.active .neochat-widget-button {
            display: none !important;
          }
          .neochat-intermediate {
            opacity: 0.9 !important;
            font-style: italic !important;
            border-left: 3px solid #4b5563 !important;
          }
          .neochat-close-button {
            position: absolute !important;
            top: 10px !important; 
            right: 10px !important;
            z-index: 10001 !important;
            color: white !important;
          }
        </style>
      </head>
      <body>
        <div id="neochat-container"></div>
        
        <script src="${sdkUrl || `\${window.location.origin}/sdk/\${projectId}.js`}"></script>
        <script>
          // Handle keyboard events and fix positioning issues
          document.addEventListener('DOMContentLoaded', function() {
            let isTyping = false;
            
            // Store user data in a global variable for access throughout the app
            window.userInfo = {
              id: window.flutterUserData?.id || 'flutter-user-123',
              name: window.flutterUserData?.name || 'John Doe',
              email: window.flutterUserData?.email || 'john@example.com',
            };
            
            console.log('Setting up NeoChat with user data:', window.userInfo);
            
                            // Initialize the chat with proper configuration
                if (window.NeoChat) {
                  // First explicitly set the user
                  if (!window.NeoChat.userData && window.flutterUserData) {
                    window.NeoChat.userData = window.flutterUserData;
                    window.NeoChat.setUser(window.flutterUserData);
                    console.log('User data set explicitly:', window.flutterUserData);
                  }
                  
                  // OPTION 1: Widget mode (with floating button)
                  /*
                  window.NeoChat.init({
                    projectId: '${projectId}',
                    container: '#neochat-container',
                    position: 'bottom-right',
                    theme: 'light',
                    showCloseButton: true,
                    user: window.flutterUserData || window.userInfo,
                    onIntermediateMessage: function(message) {
                      window.NeoChatFlutter.postMessage(JSON.stringify({
                        type: 'intermediate_message',
                        content: message.content,
                        timestamp: new Date().toISOString()
                      }));
                    }
                  });
                  */
                  
                  // OPTION 2: Embedded mode (recommended for Flutter - full control)
                  window.NeoChat.init({
                    projectId: '${projectId}',
                    container: '#neochat-container',
                    mode: 'embedded', // No default styling - Flutter controls everything
                    user: window.flutterUserData || window.userInfo,
                    onMessage: function(message) {
                      window.NeoChatFlutter.postMessage(JSON.stringify({
                        type: 'message',
                        content: message.content,
                        role: message.role,
                        timestamp: new Date().toISOString()
                      }));
                    },
                    onIntermediateMessage: function(message) {
                      window.NeoChatFlutter.postMessage(JSON.stringify({
                        type: 'intermediate_message',
                        content: message.content,
                        timestamp: new Date().toISOString()
                      }));
                    },
                    onTyping: function() {
                      window.NeoChatFlutter.postMessage(JSON.stringify({
                        type: 'typing_indicator',
                        timestamp: new Date().toISOString()
                      }));
                    }
                  });
              
              // Double check user data is set after initialization
              setTimeout(function() {
                if (window.NeoChat && window.NeoChat.userData !== window.flutterUserData) {
                  console.log('Resetting user data after initialization');
                  window.NeoChat.userData = window.flutterUserData || window.userInfo;
                  window.NeoChat.setUser(window.flutterUserData || window.userInfo);
                }
                
                // Force a greeting if needed
                if (window.NeoChat && window.NeoChat.sendSystemMessage) {
                  try {
                    // Trigger a greeting programmatically
                    window.NeoChat.sendSystemMessage("greeting");
                    console.log('Manually triggered greeting');
                  } catch (e) {
                    console.error('Error triggering greeting:', e);
                  }
                }
                
                // Log current state
                window.NeoChatFlutter.postMessage(JSON.stringify({
                  type: 'debug_sdk_state',
                  hasUserData: !!window.NeoChat.userData,
                  userData: window.NeoChat.userData,
                  timestamp: new Date().toISOString()
                }));
              }, 1000);
            }
            
            // Observe DOM for chat components
            const observer = new MutationObserver(function(mutations) {
              const widgetButton = document.querySelector('.neochat-widget-button');
              const chatContainer = document.querySelector('.neochat-chat-container');
              const closeButton = document.querySelector('.neochat-close-button');
              const chatMessages = document.querySelector('.neochat-chat-messages');
              const chatHeader = document.querySelector('.neochat-chat-header');
              const typingIndicator = document.querySelector('.neochat-typing-indicator');
              
              if (chatHeader) {
                // Ensure header has proper styling
                chatHeader.style.backgroundColor = '#4a90e2';
                chatHeader.style.color = 'white';
              }
              
              if (widgetButton) {
                // Fix widget button position
                widgetButton.style.position = 'fixed';
                widgetButton.style.bottom = '20px';
                widgetButton.style.right = '20px';
                widgetButton.style.transform = 'none';
                widgetButton.style.zIndex = '9999';
                
                // Add click handler to show chat
                widgetButton.addEventListener('click', function() {
                  if (chatContainer) {
                    chatContainer.classList.add('active');
                    // Hide the button when chat is open
                    widgetButton.style.display = 'none';
                    // Ensure scrolling works
                    if (chatMessages) {
                      chatMessages.style.overflowY = 'auto';
                      chatMessages.style.webkitOverflowScrolling = 'touch';
                      // Scroll to bottom
                      setTimeout(function() {
                        chatMessages.scrollTop = chatMessages.scrollHeight;
                      }, 100);
                    }
                  }
                });
              }
              
              if (closeButton) {
                // Handle close button click to ensure widget button reappears
                closeButton.addEventListener('click', function() {
                  if (chatContainer) {
                    chatContainer.classList.remove('active');
                  }
                  setTimeout(function() {
                    if (widgetButton) {
                      widgetButton.style.display = 'flex';
                      widgetButton.style.position = 'fixed';
                      widgetButton.style.bottom = '20px';
                      widgetButton.style.right = '20px';
                    }
                  }, 100);
                });
              }
            });
            
            observer.observe(document.body, { childList: true, subtree: true });
            
            // Handle keyboard opening and closing
            window.addEventListener('resize', function() {
              const chatMessages = document.querySelector('.neochat-chat-messages');
              const chatInput = document.querySelector('.neochat-message-input');
              const chatContainer = document.querySelector('.neochat-chat-container');
              const widgetButton = document.querySelector('.neochat-widget-button');
              
              if (chatInput && chatInput === document.activeElement) {
                // Keyboard is likely open, ensure scrolling works
                if (chatMessages) {
                  chatMessages.style.overflowY = 'auto';
                  chatMessages.style.webkitOverflowScrolling = 'touch';
                  
                  // Scroll to bottom when keyboard appears
                  setTimeout(function() {
                    chatMessages.scrollTop = chatMessages.scrollHeight;
                  }, 300);
                }
              }
            });
            
            // Listen for intermediate messages and typing events
            window.addEventListener('neochat:intermediate', function(event) {
              window.NeoChatFlutter.postMessage(JSON.stringify({
                type: 'intermediate_message',
                content: event.detail.message,
                timestamp: new Date().toISOString()
              }));
            });
            
            window.addEventListener('neochat:typing', function(event) {
              isTyping = true;
              const typingIndicator = document.querySelector('.neochat-typing-indicator');
              if (typingIndicator) {
                typingIndicator.classList.add('active');
                // Scroll to bottom to show typing indicator
                const chatMessages = document.querySelector('.neochat-chat-messages');
                if (chatMessages) {
                  setTimeout(function() {
                    chatMessages.scrollTop = chatMessages.scrollHeight;
                  }, 100);
                }
              }
              window.NeoChatFlutter.postMessage(JSON.stringify({
                type: 'typing_indicator',
                timestamp: new Date().toISOString()
              }));
            });
            
            // Listen for message events to hide typing indicator
            window.addEventListener('neochat:message', function(event) {
              isTyping = false;
              const typingIndicator = document.querySelector('.neochat-typing-indicator');
              if (typingIndicator) {
                typingIndicator.classList.remove('active');
              }
            });
            
            // Also hide typing indicator when bot message is added
            const messageObserver = new MutationObserver(function(mutations) {
              mutations.forEach(function(mutation) {
                if (mutation.addedNodes.length) {
                  mutation.addedNodes.forEach(function(node) {
                    if (node.classList && node.classList.contains('neochat-bot-message')) {
                      // Only hide typing indicator if it's not an intermediate message
                      if (!node.classList.contains('neochat-intermediate')) {
                        isTyping = false;
                        const typingIndicator = document.querySelector('.neochat-typing-indicator');
                        if (typingIndicator) {
                          typingIndicator.classList.remove('active');
                        }
                      }
                    }
                  });
                }
              });
            });
            
            // Start observing the chat messages container
            setTimeout(function() {
              const chatMessages = document.querySelector('.neochat-chat-messages');
              if (chatMessages) {
                messageObserver.observe(chatMessages, { childList: true, subtree: true });
              }
            }, 1000);
          });
        </script>
      </body>
      </html>
    ''';
  }

  @override
  Widget build(BuildContext context) {
    return Scaffold(
      appBar: AppBar(
        title: const Text('NeoChat'),
        actions: [
          IconButton(
            icon: const Icon(Icons.code),
            onPressed: _showConsoleLogDialog,
          ),
        ],
      ),
      body: WebViewWidget(controller: controller),
    );
  }
  
  void _showConsoleLogDialog() {
    showDialog(
      context: context,
      builder: (context) => AlertDialog(
        title: const Text('Console Logs'),
        content: SizedBox(
          width: double.maxFinite,
          height: 400,
          child: consoleLogs.isEmpty
              ? const Center(child: Text('No logs yet'))
              : ListView.builder(
                  itemCount: consoleLogs.length,
                  itemBuilder: (context, index) {
                    try {
                      // Try to parse as JSON for better formatting
                      final dynamic jsonObj = json.decode(consoleLogs[index]);
                      String displayText;
                      
                      if (jsonObj is Map && jsonObj.containsKey('type')) {
                        if (jsonObj['type'] == 'bot_message') {
                          displayText = '🤖 \${jsonObj["content"]}';
                        } else if (jsonObj['type'] == 'intermediate_message') {
                          displayText = '🔄 \${jsonObj["content"]}';
                        } else if (jsonObj['type'] == 'typing_indicator') {
                          displayText = '⌨️ Chatbot is typing...';
                        } else if (jsonObj['type'] == 'api_response') {
                          displayText = '📡 Response (\${jsonObj["status"]}): \${jsonObj["url"]}';
                        } else if (jsonObj['type'] == 'api_request') {
                          displayText = '📤 \${jsonObj["method"]} \${jsonObj["url"]}';
                        } else if (jsonObj['type'] == 'debug_init_params') {
                          displayText = '🔍 Init params: ID=\${jsonObj["userId"]}, Name=\${jsonObj["name"]}';
                        } else if (jsonObj['type'] == 'user_data_set') {
                          displayText = '👤 User data set: \${jsonObj["userData"]["name"]}';
                        } else if (jsonObj['type'] == 'api_request_body') {
                          displayText = '📋 Request body: \${jsonObj["url"]}';
                        } else if (jsonObj['type'] == 'error') {
                          displayText = '❌ ERROR: \${jsonObj["message"]}';
                        } else {
                          displayText = consoleLogs[index];
                        }
                      } else {
                        displayText = consoleLogs[index];
                      }
                      
                      return Padding(
                        padding: const EdgeInsets.symmetric(vertical: 4.0),
                        child: Text(
                          displayText,
                          style: const TextStyle(fontSize: 12),
                        ),
                      );
                    } catch (e) {
                      // Not JSON, just display the raw text
                      return Padding(
                        padding: const EdgeInsets.symmetric(vertical: 4.0),
                        child: Text(
                          consoleLogs[index],
                          style: const TextStyle(fontSize: 12),
                        ),
                      );
                    }
                  },
                ),
        ),
        actions: [
          TextButton(
            onPressed: () {
              setState(() {
                consoleLogs.clear();
              });
              Navigator.of(context).pop();
            },
            child: const Text('Clear'),
          ),
          TextButton(
            onPressed: () {
              Navigator.of(context).pop();
            },
            child: const Text('Close'),
          ),
        ],
      ),
    );
  }
}

// Usage example:
// Navigator.push(
//   context,
//   MaterialPageRoute(builder: (context) => const NeoChatScreen(
//     userId: 'flutter-user-123',
//     name: 'John Doe',
//     userEmail: 'john@example.com',
//   )),
// );
  `.trim();

  const getWhatsAppSnippet = () => {
    switch(whatsappIntegrationType) {
      case 'cloud':
        return `
// WhatsApp Cloud API Integration

1. Copy this webhook URL and add it to your WhatsApp Manager:
${whatsappConfig.webhookUrl || 'https://your-domain.com/webhook/whatsapp'}

2. Add the following verification token to your WhatsApp Manager:
${whatsappConfig.webhookSecret || 'YOUR_WEBHOOK_SECRET'}

3. Your WhatsApp messages will now be automatically processed by NeoChat.
No additional code is required as the integration is handled by our platform.

4. To test the integration:
- Send a message to your WhatsApp Business number
- The message will be processed by NeoChat
- The response will be sent back to the user via WhatsApp

Note: Make sure your WhatsApp Business account is properly configured
and has the necessary permissions to send and receive messages.
        `.trim();
      case 'twilio':
        return `
// Twilio WhatsApp Integration

1. First, set up a webhook endpoint in your backend:

app.post('/webhook/whatsapp', async (req, res) => {
  const { message, sender, timestamp } = req.body;
  
  // Initialize NeoChat API client
  const neochat = new NeoChatAPI({
    projectId: '${projectId}',
    apiKey: 'YOUR_API_KEY'
  });
  
  // Send message to NeoChat
  const response = await neochat.sendMessage({
    message,
    sessionId: sender, // Use sender's phone as session ID
    user: {
      id: sender,
      name: 'WhatsApp User'
    }
  });
  
  // Send response back to WhatsApp
  await sendWhatsAppMessage(sender, response.message);
  
  res.status(200).send('OK');
});

2. Connect to Twilio WhatsApp API

// Example with Twilio:
const twilioClient = require('twilio')(TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN);

async function sendWhatsAppMessage(to, message) {
  await twilioClient.messages.create({
    body: message,
    from: 'whatsapp:+1XXXYYYZZZZ', // Your WhatsApp business number
    to: \`whatsapp:\${to}\`
  });
}
        `.trim();
      case 'qontak':
        return `
// Qontak WhatsApp Integration

1. First, set up a webhook endpoint in your backend:

app.post('/webhook/whatsapp', async (req, res) => {
  const { message, sender, timestamp } = req.body;
  
  // Initialize NeoChat API client
  const neochat = new NeoChatAPI({
    projectId: '${projectId}',
    apiKey: 'YOUR_API_KEY'
  });
  
  // Send message to NeoChat
  const response = await neochat.sendMessage({
    message,
    sessionId: sender, // Use sender's phone as session ID
    user: {
      id: sender,
      name: 'WhatsApp User'
    }
  });
  
  // Send response back to WhatsApp
  await sendWhatsAppMessage(sender, response.message);
  
  res.status(200).send('OK');
});

2. Connect to Qontak WhatsApp API

// Example with Qontak:
const axios = require('axios');

async function sendWhatsAppMessage(to, message) {
  await axios.post('https://api.qontak.com/v1/whatsapp/messages', {
    phone_number: to,
    message: message,
    channel_id: 'YOUR_CHANNEL_ID'
  }, {
    headers: {
      'Authorization': 'Bearer YOUR_QONTAK_TOKEN',
      'Content-Type': 'application/json'
    }
  });
}
        `.trim();
      default:
        return '';
    }
  };

  const getEmbeddedSnippet = () => `
<!-- Embedded/Headless Integration -->
<!-- This integration provides just the chat functionality without any styling or container -->

<div id="your-custom-chat-container" style="width: 100%; height: 400px; border: 1px solid #ccc;">
  <!-- This div will contain only the chat messages and input - no headers, no styling -->
  <div id="neochat-embedded"></div>
</div>

<script src="${sdkUrl || `${window.location.origin}/sdk/${projectId}.js`}"></script>
<script>
  // Initialize in embedded/headless mode
  NeoChat.init({
    projectId: '${projectId}',
    container: '#neochat-embedded',
    mode: 'embedded', // This removes all default styling and containers
    // No position, theme, height, width needed - you control the styling
    
    // Optional: Handle all events to build your own UI
    onMessage: function(message) {
      console.log('New message:', message);
      // Build your own message display logic
    },
    onIntermediateMessage: function(message) {
      console.log('Intermediate message:', message.content);
      // Handle intermediate messages in your own UI
    },
    onTyping: function() {
      console.log('Bot is typing...');
      // Show your own typing indicator
    },
    onOpen: function() {
      console.log('Chat opened');
    },
    onClose: function() {
      console.log('Chat closed');
    }
  });

  // Set user information
  NeoChat.setUser({
    id: 'user123',
    name: 'John Doe',
    email: 'john@example.com',
    metadata: {
      token: 'your-jwt-token-here',
      apiKey: 'your-api-key-here'
    }
  });

  // In embedded mode, you can also manually send messages programmatically
  // NeoChat.sendMessage('Hello from my custom interface!');
  
  // Check if waiting for input (useful for Flow Builder integration)
  // if (NeoChat.isWaitingForInput()) {
  //   const inputStep = NeoChat.getCurrentInputStep();
  //   console.log('Waiting for input:', inputStep);
  // }
</script>

<!-- Custom CSS for your own styling (100% width as requested) -->
<style>
#neochat-embedded {
  width: 100% !important;
  height: 100% !important;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
}

/* Style the chat messages container */
#neochat-embedded .neochat-chat-messages {
  width: 100% !important;
  padding: 10px !important;
  max-height: calc(100% - 60px) !important;
  overflow-y: auto !important;
}

/* Style the input container */
#neochat-embedded .neochat-chat-footer {
  width: 100% !important;
  padding: 10px !important;
  border: none !important;
  border-top: 1px solid #eee !important;
}

/* Hide any default headers or decorations */
#neochat-embedded .neochat-chat-header,
#neochat-embedded .neochat-widget-button,
#neochat-embedded .neochat-close-button {
  display: none !important;
}

/* Custom message styling (optional) */
#neochat-embedded .neochat-message {
  margin-bottom: 10px !important;
}

#neochat-embedded .neochat-bot-message {
  background-color: #f1f1f1 !important;
  color: #333 !important;
}

#neochat-embedded .neochat-user-message {
  background-color: #007bff !important;
  color: white !important;
}
</style>
  `.trim();

  const getVueSnippet = (isEmbedded) => `
// Vue Integration Example

// First, create a new Vue component for the chat widget
// components/NeoChatWidget.vue

<template>
  <div>
    <div id="neochat-container" ref="container"></div>
    <!-- Optional: Display intermediate message separately -->
    <div v-if="intermediateMessage" class="intermediate-message-container">
      {{ intermediateMessage.content }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'NeoChatWidget',
  props: {
    userId: {
      type: String,
      default: 'user123'
    },
    userName: {
      type: String,
      default: 'John Doe'
    },
    userEmail: {
      type: String,
      default: 'john@example.com'
    }
  },
  data() {
    return {
      scriptLoaded: false,
      intermediateMessage: null
    }
  },
  mounted() {
    this.loadNeoChatScript()
  },
  beforeUnmount() {
    // Clean up event listeners
    window.removeEventListener('neochat:intermediate', this.handleIntermediateMessage)
    window.removeEventListener('neochat:typing', this.handleTypingIndicator)
  },
  methods: {
    loadNeoChatScript() {
      if (!this.scriptLoaded) {
        const script = document.createElement('script')
        script.src = "${sdkUrl || `${window.location.origin}/sdk/${projectId}.js`}"
        script.async = true
        script.onload = () => {
          this.initializeNeoChat()
          this.scriptLoaded = true
        }
        document.body.appendChild(script)
      }
    },
    initializeNeoChat() {
      if (window.NeoChat) {
        // Set up event listeners for intermediate messages and typing
        window.addEventListener('neochat:intermediate', this.handleIntermediateMessage)
        window.addEventListener('neochat:typing', this.handleTypingIndicator)
        
        // Initialize the SDK
        window.NeoChat.init({
          projectId: '${projectId}',
          container: '#neochat-container',
          position: 'bottom-right',
          theme: 'light',
          onIntermediateMessage: (message) => {
            this.intermediateMessage = message
          }
        })

        // Set user information if provided
        if (this.userId) {
          window.NeoChat.setUser({
            id: this.userId,
            name: this.userName,
            email: this.userEmail,
            metadata: {
              // Add any additional user data here
              // Authentication data for API endpoints (if configured)
              token: 'your-jwt-token-here',
              apiKey: 'your-api-key-here'
            }
          })
        }
      }
    },
    // Handlers for custom events
    handleIntermediateMessage(event) {
      if (event.detail && event.detail.message) {
        this.intermediateMessage = event.detail.message
        console.log('Intermediate message received:', this.intermediateMessage.content)
      }
    },
    handleTypingIndicator() {
      console.log('Chatbot is typing...')
      // You can update UI to show a custom typing indicator
    }
  }
}
</script>

<style scoped>
#neochat-container {
  width: 100%;
  height: 100%;
}
.intermediate-message-container {
  border-left: 3px solid #4b5563;
  padding: 8px 12px;
  margin: 10px 0;
  background-color: #f9fafb;
  font-style: italic;
}
</style>

// Usage in your Vue app:
// App.vue or any other component

<template>
  <div class="app">
    <h1>My Vue App with NeoChat</h1>
    <NeoChatWidget 
      userId="user123"
      userName="John Doe"
      userEmail="john@example.com"
    />
  </div>
</template>

<script>
import NeoChatWidget from './components/NeoChatWidget.vue'

export default {
  name: 'App',
  components: {
    NeoChatWidget
  }
}
</script>

// ALTERNATIVE: Vue with Embedded Mode (Recommended for custom styling)

<template>
  <div class="custom-chat-wrapper">
    <!-- Your custom header -->
    <div class="custom-header">
      <h3>{{ chatTitle }}</h3>
      <div v-if="isTyping" class="typing-indicator">Bot is typing...</div>
    </div>
    
    <!-- NeoChat embedded container - 100% width, no default styling -->
    <div id="neochat-embedded" class="chat-container"></div>
    
    <!-- Optional: Custom controls -->
    <div class="custom-controls">
      <button @click="sendQuickMessage('Hello!')" class="quick-btn">
        Quick Hello
      </button>
      <button @click="sendQuickMessage('Help me')" class="quick-btn">
        Get Help
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NeoChatEmbedded',
  props: {
    userId: { type: String, default: 'user123' },
    userName: { type: String, default: 'John Doe' },
    userEmail: { type: String, default: 'john@example.com' },
    chatTitle: { type: String, default: 'Custom Chat' }
  },
  data() {
    return {
      scriptLoaded: false,
      isTyping: false,
      messages: []
    }
  },
  mounted() {
    this.loadNeoChatScript()
  },
  beforeUnmount() {
    window.removeEventListener('neochat:typing', this.handleTyping)
    window.removeEventListener('neochat:message', this.handleMessage)
  },
  methods: {
    loadNeoChatScript() {
      if (!this.scriptLoaded) {
        const script = document.createElement('script')
        script.src = "${sdkUrl || `${window.location.origin}/sdk/${projectId}.js`}"
        script.async = true
        script.onload = () => {
          this.initializeNeoChat()
          this.scriptLoaded = true
        }
        document.body.appendChild(script)
      }
    },
    initializeNeoChat() {
      if (window.NeoChat) {
        // Set up event listeners
        window.addEventListener('neochat:typing', this.handleTyping)
        window.addEventListener('neochat:message', this.handleMessage)
        
        // Initialize in embedded mode
        window.NeoChat.init({
          projectId: '${projectId}',
          container: '#neochat-embedded',
          mode: 'embedded', // No default styling or headers!
          onMessage: (message) => {
            this.messages.push(message)
            this.isTyping = false
          },
          onTyping: () => {
            this.isTyping = true
          },
          onIntermediateMessage: (message) => {
            console.log('Intermediate message:', message.content)
          }
        })

        // Set user information
        if (this.userId) {
          window.NeoChat.setUser({
            id: this.userId,
            name: this.userName,
            email: this.userEmail,
            metadata: {
              token: 'your-jwt-token-here'
            }
          })
        }
      }
    },
    // Send message programmatically
    sendQuickMessage(message) {
      if (window.NeoChat) {
        window.NeoChat.sendMessage(message)
      }
    },
    handleTyping() {
      this.isTyping = true
    },
    handleMessage(event) {
      if (event.detail) {
        this.messages.push(event.detail)
      }
      this.isTyping = false
    }
  }
}
</script>

<style scoped>
.custom-chat-wrapper {
  width: 100%;
  height: 500px;
  border: 1px solid #ddd;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.custom-header {
  padding: 15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-align: center;
}

.custom-header h3 {
  margin: 0;
  font-size: 18px;
}

.typing-indicator {
  font-size: 12px;
  opacity: 0.8;
  margin-top: 5px;
}

.chat-container {
  flex: 1;
  width: 100%;
  /* NeoChat embedded will fill this container completely */
}

.custom-controls {
  padding: 10px;
  border-top: 1px solid #ddd;
  background: #f9f9f9;
  display: flex;
  gap: 10px;
}

.quick-btn {
  padding: 8px 16px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.quick-btn:hover {
  background: #5a6fd8;
}
</style>

// Usage in parent component:
// <NeoChatEmbedded 
//   userId="user123"
//   userName="John Doe" 
//   userEmail="john@example.com"
//   chatTitle="Support Chat"
// />
  `.trim();

  const getIntegrationSnippet = () => {
    switch(integrationType) {
      case 'html':
        return isEmbeddedMode ? getEmbeddedSnippet() : getHtmlSnippet();
      case 'react':
        return isEmbeddedMode ? getReactSnippet(true) : getReactSnippet(false);
      case 'flutter':
        return isEmbeddedMode ? getFlutterSnippet(true) : getFlutterSnippet(false);
      case 'whatsapp':
        return getWhatsAppSnippet();
      case 'vue':
        return isEmbeddedMode ? getVueSnippet(true) : getVueSnippet(false);
      default:
        return isEmbeddedMode ? getEmbeddedSnippet() : getHtmlSnippet();
    }
  };

  const getIntegrationTitle = () => {
    const baseTitle = (() => {
      switch(integrationType) {
        case 'html':
          return 'HTML Integration';
        case 'react':
          return 'React Integration';
        case 'flutter':
          return 'Flutter Integration';
        case 'whatsapp':
          return 'WhatsApp Integration';
        case 'vue':
          return 'Vue Integration';
        default:
          return 'HTML Integration';
      }
    })();
    
    // Add embedded mode indicator for non-WhatsApp integrations
    if (integrationType !== 'whatsapp' && isEmbeddedMode) {
      return `${baseTitle} (Embedded Mode)`;
    }
    
    return baseTitle;
  };

  // Function to handle color picker changes
  const handleColorChange = (color, setter) => {
    setter(color.hex);
  };

  // Function to toggle color picker visibility
  const toggleColorPicker = (pickerName) => {
    if (hasReadOnlyAccess) return; // Prevent opening color picker in read-only mode
    setActiveColorPicker(activeColorPicker === pickerName ? null : pickerName);
  };

  // Generate dynamic CSS for the preview
  const generatePreviewStyles = () => {
    return {
      chatContainer: {
        backgroundColor: backgroundColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '8px',
        overflow: 'hidden',
        fontFamily: fontFamily,
        color: textColor,
        fontSize: fontSize,
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        width: '100%',
        height: '400px',
        display: 'flex',
        flexDirection: 'column',
      },
      header: {
        backgroundColor: primaryColor,
        color: buttonTextColor,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
      },
      headerLogo: {
        width: '24px',
        height: '24px',
        marginRight: '8px',
        borderRadius: '50%',
        overflow: 'hidden',
      },
      chatBody: {
        padding: '16px',
        flex: 1,
        overflowY: 'auto',
        backgroundColor: backgroundColor,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      },
      botMessage: {
        backgroundColor: secondaryColor,
        color: textColor,
        padding: '12px',
        borderRadius: '16px 16px 16px 0',
        maxWidth: '80%',
        alignSelf: 'flex-start',
      },
      userMessage: {
        backgroundColor: primaryColor,
        color: buttonTextColor,
        padding: '12px',
        borderRadius: '16px 16px 0 16px',
        maxWidth: '80%',
        alignSelf: 'flex-end',
      },
      inputContainer: {
        display: 'flex',
        padding: '12px',
        borderTop: `1px solid ${borderColor}`,
        backgroundColor: backgroundColor,
      },
      input: {
        flex: 1,
        padding: '8px 12px',
        border: `1px solid ${borderColor}`,
        borderRadius: '20px',
        marginRight: '8px',
        outline: 'none',
        fontSize: fontSize,
        fontFamily: fontFamily,
        color: textColor,
      },
      button: {
        backgroundColor: buttonColor,
        color: buttonTextColor,
        border: 'none',
        borderRadius: '50%',
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      },
    };
  };

  const styles = generatePreviewStyles();

  // Generate SDK script
  const generateSDKScript = async () => {
    setIsGeneratingSDK(true);
    try {
      // Force regeneration of the SDK script
      const result = await projectService.getSDKScript(projectId, true);
      
      if (result && result.sdkUrl) {
        setSdkUrl(result.sdkUrl);
        toast.success('SDK script generated successfully!', {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        throw new Error('Failed to generate SDK script');
      }
    } catch (error) {
      console.error('Error generating SDK script:', error);
      toast.error('Failed to generate SDK script. Please try again.', {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsGeneratingSDK(false);
    }
  };

  return (
    <Layout>
      <ToastContainer />
      <div className="dashboard">
        <div className="dashboard-header mb-6">
          <h1 className="page-title">SDK Integration</h1>
          <p className="text-gray-600">Customize your chatbot's appearance and integration options.</p>
        </div>

        <div className="space-y-6">
          {/* Customization Panel */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold mb-4">Chatbot Appearance</h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 px-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Chatbot Name</label>
                    <input
                      type="text"
                      value={chatbotName}
                      onChange={(e) => setChatbotName(e.target.value)}
                      className="form-input"
                      placeholder="Assistant"
                      disabled={hasReadOnlyAccess}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Initial Message</label>
                    <input
                      type="text"
                      value={initialMessage}
                      onChange={(e) => setInitialMessage(e.target.value)}
                      className="form-input"
                      placeholder="Hi there! How can I help you today?"
                      disabled={hasReadOnlyAccess}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Input Placeholder Text</label>
                  <input
                    type="text"
                    value={placeholderText}
                    onChange={(e) => setPlaceholderText(e.target.value)}
                    className="form-input"
                    placeholder="Type your message..."
                    disabled={hasReadOnlyAccess}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    This text will appear in the message input field when it's empty.
                  </p>
                </div>

                {/* Rate Limit Message - Only show when rate limiting is enabled */}
                {isRateLimitingEnabled && (
                  <div className="form-group">
                    <label className="form-label">Rate Limit Message</label>
                    <input
                      type="text"
                      value={rateLimitMessage}
                      onChange={(e) => setRateLimitMessage(e.target.value)}
                      className="form-input"
                      placeholder="Message rate limit exceeded. Please wait before sending more messages."
                      disabled={hasReadOnlyAccess}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      This message will be shown when a user exceeds the message rate limit.
                    </p>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Logo URL</label>
                  <input
                    type="text"
                    value={logo}
                    onChange={(e) => setLogo(e.target.value)}
                    className="form-input"
                    placeholder="https://example.com/logo.png"
                    disabled={hasReadOnlyAccess}
                  />
                  <p className="mt-1 text-xs text-gray-500">Enter a URL to your logo image (recommended size: 32x32px)</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Font Family</label>
                    <select
                      value={fontFamily}
                      onChange={(e) => setFontFamily(e.target.value)}
                      className="form-select text-smd"
                      disabled={hasReadOnlyAccess}
                    >
                      <option value="Inter, sans-serif">Inter</option>
                      <option value="Arial, sans-serif">Arial</option>
                      <option value="'Helvetica Neue', sans-serif">Helvetica</option>
                      <option value="'Segoe UI', sans-serif">Segoe UI</option>
                      <option value="Roboto, sans-serif">Roboto</option>
                      <option value="'Open Sans', sans-serif">Open Sans</option>
                      <option value="Georgia, serif">Georgia</option>
                      <option value="monospace">Monospace</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Font Size</label>
                    <select
                      value={fontSize}
                      onChange={(e) => setFontSize(e.target.value)}
                      className="form-select text-smd"
                      disabled={hasReadOnlyAccess}
                    >
                      <option value="12px">Small (12px)</option>
                      <option value="14px">Medium (14px)</option>
                      <option value="16px">Large (16px)</option>
                      <option value="18px">Extra Large (18px)</option>
                    </select>
                  </div>
                </div>

                {/* Color Selectors Grid */}
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-medium mb-4">Color Scheme</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      <div className="form-group-inline">
                        <label className="form-label-semibold block mb-2">Primary Color</label>
                        <div 
                          className="mediumSquare rounded cursor-pointer border flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"
                          style={{ backgroundColor: primaryColor }}
                          onClick={() => toggleColorPicker('primary')}
                        >
                          <span className={`text-xs font-mono ${primaryColor.toLowerCase() === '#ffffff' ? 'text-black' : 'text-white'}`}>
                            {primaryColor}
                          </span>
                        </div>
                        {activeColorPicker === 'primary' && (
                          <div className="absolute z-10 mt-1">
                            <div 
                              className="fixed inset-0" 
                              onClick={() => toggleColorPicker(null)}
                            />
                            <SketchPicker 
                              color={primaryColor} 
                              onChange={(color) => handleColorChange(color, setPrimaryColor)} 
                            />
                          </div>
                        )}
                      </div>
                      <div className="form-group-inline">
                        <label className="form-label-semibold block mb-2">Secondary Color</label>
                        <div 
                          className="mediumSquare rounded cursor-pointer border flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"
                          style={{ backgroundColor: secondaryColor }}
                          onClick={() => toggleColorPicker('secondary')}
                        >
                          <span className={`text-xs font-mono ${secondaryColor.toLowerCase() === '#ffffff' ? 'text-black' : 'text-gray-800'}`}>
                            {secondaryColor}
                          </span>
                        </div>
                        {activeColorPicker === 'secondary' && (
                          <div className="absolute z-10 mt-1">
                            <div 
                              className="fixed inset-0" 
                              onClick={() => toggleColorPicker(null)}
                            />
                            <SketchPicker 
                              color={secondaryColor} 
                              onChange={(color) => handleColorChange(color, setSecondaryColor)} 
                            />
                          </div>
                        )}
                      </div>
                      <div className="form-group-inline">
                        <label className="form-label-semibold block mb-2">Background</label>
                        <div 
                          className="mediumSquare rounded cursor-pointer border flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"
                          style={{ backgroundColor: backgroundColor }}
                          onClick={() => toggleColorPicker('background')}
                        >
                          <span className={`text-xs font-mono ${backgroundColor.toLowerCase() === '#ffffff' ? 'text-black' : 'text-white'}`}>
                            {backgroundColor}
                          </span>
                        </div>
                        {activeColorPicker === 'background' && (
                          <div className="absolute z-10 mt-1">
                            <div 
                              className="fixed inset-0" 
                              onClick={() => toggleColorPicker(null)}
                            />
                            <SketchPicker 
                              color={backgroundColor} 
                              onChange={(color) => handleColorChange(color, setBackgroundColor)} 
                            />
                          </div>
                        )}
                      </div>
                      <div className="form-group-inline">
                        <label className="form-label-semibold block mb-2">Text Color</label>
                        <div 
                          className="mediumSquare rounded cursor-pointer border flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"
                          style={{ backgroundColor: textColor }}
                          onClick={() => toggleColorPicker('text')}
                        >
                          <span className={`text-xs font-mono ${textColor.toLowerCase() === '#ffffff' ? 'text-black' : 'text-white'}`}>
                            {textColor}
                          </span>
                        </div>
                        {activeColorPicker === 'text' && (
                          <div className="absolute z-10 mt-1">
                            <div 
                              className="fixed inset-0" 
                              onClick={() => toggleColorPicker(null)}
                            />
                            <SketchPicker 
                              color={textColor} 
                              onChange={(color) => handleColorChange(color, setTextColor)} 
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-medium mb-4">Additional Colors</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                      <div className="form-group-inline">
                        <label className="form-label-semibold block mb-2">Border Color</label>
                        <div 
                          className="mediumSquare rounded cursor-pointer border flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"
                          style={{ backgroundColor: borderColor }}
                          onClick={() => toggleColorPicker('border')}
                        >
                          <span className={`text-xs font-mono ${borderColor.toLowerCase() === '#ffffff' ? 'text-black' : 'text-gray-800'}`}>
                            {borderColor}
                          </span>
                        </div>
                        {activeColorPicker === 'border' && (
                          <div className="absolute z-10 mt-1">
                            <div 
                              className="fixed inset-0" 
                              onClick={() => toggleColorPicker(null)}
                            />
                            <SketchPicker 
                              color={borderColor} 
                              onChange={(color) => handleColorChange(color, setBorderColor)} 
                            />
                          </div>
                        )}
                      </div>
                      <div className="form-group-inline">
                        <label className="form-label-semibold block mb-2">Button Color</label>
                        <div 
                          className="mediumSquare rounded cursor-pointer border flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"
                          style={{ backgroundColor: buttonColor }}
                          onClick={() => toggleColorPicker('button')}
                        >
                          <span className={`text-xs font-mono ${buttonColor.toLowerCase() === '#ffffff' ? 'text-black' : 'text-white'}`}>
                            {buttonColor}
                          </span>
                        </div>
                        {activeColorPicker === 'button' && (
                          <div className="absolute z-10 mt-1">
                            <div 
                              className="fixed inset-0" 
                              onClick={() => toggleColorPicker(null)}
                            />
                            <SketchPicker 
                              color={buttonColor} 
                              onChange={(color) => handleColorChange(color, setButtonColor)} 
                            />
                          </div>
                        )}
                      </div>
                      <div className="form-group-inline">
                        <label className="form-label-semibold block mb-2">Button Text</label>
                        <div 
                          className="mediumSquare rounded cursor-pointer border flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"
                          style={{ backgroundColor: buttonTextColor }}
                          onClick={() => toggleColorPicker('buttonText')}
                        >
                          <span className={`text-xs font-mono ${buttonTextColor.toLowerCase() === '#ffffff' ? 'text-black' : 'text-white'}`}>
                            {buttonTextColor}
                          </span>
                        </div>
                        {activeColorPicker === 'buttonText' && (
                          <div className="absolute z-10 mt-1">
                            <div 
                              className="fixed inset-0" 
                              onClick={() => toggleColorPicker(null)}
                            />
                            <SketchPicker 
                              color={buttonTextColor} 
                              onChange={(color) => handleColorChange(color, setButtonTextColor)} 
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Advanced: Custom CSS</label>
                  <textarea
                    value={customCss}
                    onChange={(e) => setCustomCss(e.target.value)}
                    className="form-textarea h-24 font-mono text-sm w-full h-100"
                    placeholder="Enter custom CSS to further style your chatbot widget..."
                  />
                  <p className="mt-1 text-xs text-gray-500">For advanced users only. Custom CSS will be applied on top of the styling options above.</p>
                </div>

                {hasReadOnlyAccess && (
                  <ReadOnlyIndicator />
                )}

                {!hasReadOnlyAccess && (
                  <div className="mt-6">
                    <button 
                      type="submit" 
                      className="btn btn-primary text-smd"
                      disabled={isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Save Configuration'}
                    </button>
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Preview Panel */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold mb-4">Live Preview</h2>
            <p className="text-sm text-gray-600 mb-4">
              This is how your chatbot will appear to your users. Changes you make will be reflected in real-time.
            </p>
            <div style={styles.chatContainer}>
              <div style={styles.header}>
                {logo && (
                  <div style={styles.headerLogo}>
                    <img src={logo} alt="Logo" width="24" height="24" />
                  </div>
                )}
                <div>{chatbotName}</div>
              </div>
              <div style={styles.chatBody}>
                <div style={styles.botMessage}>
                  {initialMessage}
                </div>
                <div style={styles.userMessage}>
                  Hello! I'd like to know more about your services.
                </div>
                <div style={styles.botMessage}>
                  I'd be happy to tell you about our services! We offer a wide range of solutions including AI chatbots, knowledge base integration, and custom APIs.
                </div>
              </div>
              <div style={styles.inputContainer}>
                <input
                  type="text"
                  placeholder="Type your message..."
                  style={styles.input}
                />
                <button style={styles.button}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 2L11 13" />
                    <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Integration Options Panel */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold mb-4">Integration Options</h2>
            <div className="space-y-6">
              <div className="form-group">
                <label className="form-label">Select Integration Type</label>
                <select
                  value={integrationType}
                  onChange={(e) => {
                    setIntegrationType(e.target.value);
                    // Reset embedded mode when switching to WhatsApp
                    if (e.target.value === 'whatsapp') {
                      setIsEmbeddedMode(false);
                    }
                  }}
                  className="form-select"
                >
                  <option value="html">HTML Integration</option>
                  <option value="react">React Integration</option>
                  <option value="vue">Vue Integration</option>
                  <option value="flutter">Flutter Integration</option>
                  <option value="whatsapp">WhatsApp Integration</option>
                </select>
              </div>

              {/* Embedded Mode Toggle - Only show for non-WhatsApp integrations */}
              {integrationType !== 'whatsapp' && (
                <div className="form-group">

                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex-1">
                      <label className="form-label mb-1">Embedded Mode</label>
                      <p className="text-sm text-gray-600">
                        Enable embedded mode for complete control over chat styling. 
                        {isEmbeddedMode ? ' Chat will render without default headers or styling.' : ' Chat will use default widget styling with floating button.'}
                      </p>
                    </div>
                    <div className="ml-4">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isEmbeddedMode}
                          onChange={(e) => setIsEmbeddedMode(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {isEmbeddedMode ? 'Enabled' : 'Disabled'}
                        </span>
                      </label>
                    </div>
                  </div>
                  
                  {isEmbeddedMode && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <h4 className="text-sm font-medium text-green-800">Embedded Mode Benefits:</h4>
                          <ul className="mt-1 text-xs text-green-700 space-y-1">
                            <li>• 100% width container (fills parent element)</li>
                            <li>• No default headers, buttons, or decorative elements</li>
                            <li>• Complete styling control by your application</li>
                            <li>• Professional appearance with custom branding</li>
                            <li>• Better integration with frameworks like React, Vue, Flutter</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div>
                <h3 className="text-lg font-medium mb-2">{getIntegrationTitle()}</h3>
                
                {/* Show SDK URL info if available */}
                {sdkUrl && <SDKUrlInfo />}
                
                {/* Important note about SDK compatibility */}
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                  <h4 className="font-medium text-blue-800 mb-2">💡 Important Notes:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• <strong>Same SDK for all modes:</strong> You don't need to generate a new SDK script. The same SDK works for both widget mode and embedded mode.</li>
                    <li>• <strong>Embedded mode recommended:</strong> For React, Vue, Flutter, and other frameworks, embedded mode gives you better control over styling and integration.</li>
                    <li>• <strong>Just change the init parameters:</strong> Switch between modes by using <code>mode: 'widget'</code> (default) or <code>mode: 'embedded'</code>.</li>
                    <li>• <strong>Dynamic ProjectId:</strong> You can pass any <code>projectId</code> in <code>NeoChat.init()</code> to override the SDK's default projectId.</li>
                  </ul>
                </div>

                {/* Dynamic ProjectId Feature */}
                <div className="bg-green-50 border border-green-200 rounded p-3 mb-4">
                  <h4 className="font-medium text-green-800 mb-2">🔧 Dynamic ProjectId Support:</h4>
                  <p className="text-sm text-green-700 mb-2">
                    Your client can use the same SDK script across multiple projects by passing different projectId values:
                  </p>
                  <pre className="bg-white p-2 rounded text-xs font-mono text-gray-800 border">
{`// Use the same SDK script for different projects
NeoChat.init({
  projectId: 'project-a-id',  // Override SDK's default
  container: '#chat-container'
});

// Or dynamically determine the projectId
const currentProject = getCurrentUserProject(); // Your logic
NeoChat.init({
  projectId: currentProject.id,
  container: '#chat-container'
});`}
                  </pre>
                  <p className="text-xs text-green-600 mt-2">
                    This allows one SDK script to work with multiple NeoChat projects.
                  </p>
                </div>
                
                <p className="text-sm text-gray-600 mb-2 mt-2">
                  Follow these instructions to integrate the chatbot:
                </p>
                <pre className="bg-gray-50 p-4 rounded border overflow-x-auto text-sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {getIntegrationSnippet()}
                </pre>
                <div className="flex space-x-2 mt-2">
                  <button
                    className="btn btn-secondary text-smd"
                    onClick={() => {
                      navigator.clipboard.writeText(getIntegrationSnippet());
                      toast.info('Code copied to clipboard!', {
                        position: "top-right",
                        autoClose: 2000,
                      });
                    }}
                  >
                    Copy Code
                  </button>
                  
                  {!hasReadOnlyAccess && (
                    <button
                      className="btn btn-primary text-smd ml-2"
                      onClick={generateSDKScript}
                      disabled={isGeneratingSDK}
                    >
                      {isGeneratingSDK ? 'Generating...' : 'Generate SDK Script'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* API Reference Panel */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold mb-4">API Reference</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium">NeoChat.init(config)</h3>
                <p className="text-sm text-gray-600">
                  Initialize the chatbot with your configuration.
                </p>
                <pre className="bg-gray-50 p-2 rounded mt-1 text-sm">
                  {`
{
  projectId: string,    // Your project ID (overrides SDK's default)
  container: string,    // CSS selector for the container
  position: string,     // 'bottom-right' or 'bottom-left'
  theme: string,        // 'light' or 'dark'
  height?: number,      // Widget height in pixels
  width?: number,       // Widget width in pixels
  mode?: string,        // 'widget' (default) or 'embedded'
}
                  `.trim()}
                </pre>
                <p className="text-xs text-gray-500 mt-1">
                  <strong>Note:</strong> The <code>projectId</code> parameter will override the SDK's hardcoded project ID, 
                  allowing you to use the same SDK script for multiple projects.
                </p>
              </div>

              <div>
                <h3 className="font-medium">NeoChat.setUser(user)</h3>
                <p className="text-sm text-gray-600">
                  Set user information for personalized interactions.
                </p>
                <pre className="bg-gray-50 p-2 rounded mt-1 text-sm">
                  {`
{
  id: string,          // Unique user ID
  name?: string,       // User's name
  email?: string,      // User's email
  metadata?: {         // Additional user data
    // Optional: authentication data for API endpoints (if configured)
    token?: string,    // JWT/Bearer token
    apiKey?: string,   // API key value
    [key: string]: any // Any custom fields used by your API endpoints
  }
}
                  `.trim()}
                </pre>
              </div>

              <div>
                <h3 className="font-medium">Embedded Mode Methods</h3>
                <p className="text-sm text-gray-600 mb-2">
                  Additional methods available for programmatic control (especially useful in embedded mode):
                </p>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-sm">NeoChat.sendMessage(message)</h4>
                    <p className="text-xs text-gray-600">Send a message programmatically</p>
                    <pre className="bg-gray-50 p-2 rounded mt-1 text-xs">
                      {`NeoChat.sendMessage('Hello from my custom interface!');`}
                    </pre>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-sm">NeoChat.isOpen() / open() / close()</h4>
                    <p className="text-xs text-gray-600">Control widget visibility (no effect in embedded mode)</p>
                    <pre className="bg-gray-50 p-2 rounded mt-1 text-xs">
                      {`
if (!NeoChat.isOpen()) {
  NeoChat.open();  // Open the widget
}
NeoChat.close(); // Close the widget
                      `.trim()}
                    </pre>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-sm">Flow Builder Methods</h4>
                    <p className="text-xs text-gray-600">Check Flow Builder state for input collection</p>
                    <pre className="bg-gray-50 p-2 rounded mt-1 text-xs">
                      {`
if (NeoChat.isWaitingForInput()) {
  const step = NeoChat.getCurrentInputStep();
  console.log('Waiting for:', step);
}

const state = NeoChat.getFlowBuilderState();
                      `.trim()}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SDKIntegration; 